import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Display,
  Heading,
  Text,
  Stack,
  Container,
} from "@/components/ui/index.js";
import { WelcomeStep } from "./components/WelcomeStep/WelcomeStep.jsx";
import { SportSelectionStep } from "./components/SportSelectionStep/SportSelectionStep.jsx";
import { SkillLevelStep } from "./components/SkillLevelStep/SkillLevelStep.jsx";
import { LocationStep } from "./components/LocationStep/LocationStep.jsx";
import { CompleteStep } from "./components/CompleteStep/CompleteStep.jsx";
import { ProgressBar } from "./components/ProgressBar/ProgressBar.jsx";
import styles from "./OnboardingPage.module.css";

/**
 * OnboardingPage - Multi-step welcome flow for new users
 *
 * Features:
 * - 5-step onboarding process
 * - Sport preference selection with animated cards
 * - Skill level selection
 * - Location input
 * - Smooth transitions between steps
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedSports, setSelectedSports] = useState([]);
  const [skillLevel, setSkillLevel] = useState(null);
  const [location, setLocation] = useState("");
  const [direction, setDirection] = useState("forward");

  const totalSteps = 5;

  const handleNext = () => {
    if (step < totalSteps) {
      setDirection("forward");
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setDirection("backward");
      setStep(step - 1);
    }
  };

  const handleSportToggle = (sportId) => {
    setSelectedSports((prev) => {
      if (prev.includes(sportId)) {
        return prev.filter((id) => id !== sportId);
      }
      return [...prev, sportId];
    });
  };

  const handleSkillSelect = (level) => {
    setSkillLevel(level);
  };

  const handleLocationChange = (value) => {
    setLocation(value);
  };

  const handleComplete = () => {
    navigate("/discover");
  };

  const getStepAnimationClass = () => {
    if (direction === "forward") {
      return styles.slideInFromRight;
    }
    return styles.slideInFromLeft;
  };

  const renderStep = () => {
    const animationClass = getStepAnimationClass();

    switch (step) {
      case 1:
        return (
          <div
            className={`${styles.stepContent} ${animationClass}`}
            key="step1"
          >
            <WelcomeStep onGetStarted={handleNext} />
          </div>
        );
      case 2:
        return (
          <div
            className={`${styles.stepContent} ${animationClass}`}
            key="step2"
          >
            <SportSelectionStep
              selectedSports={selectedSports}
              onSportToggle={handleSportToggle}
              onContinue={handleNext}
              onBack={handleBack}
            />
          </div>
        );
      case 3:
        return (
          <div
            className={`${styles.stepContent} ${animationClass}`}
            key="step3"
          >
            <SkillLevelStep
              selectedLevel={skillLevel}
              onLevelSelect={handleSkillSelect}
              onContinue={handleNext}
              onBack={handleBack}
            />
          </div>
        );
      case 4:
        return (
          <div
            className={`${styles.stepContent} ${animationClass}`}
            key="step4"
          >
            <LocationStep
              location={location}
              onLocationChange={handleLocationChange}
              onContinue={handleNext}
              onBack={handleBack}
            />
          </div>
        );
      case 5:
        return (
          <div
            className={`${styles.stepContent} ${animationClass}`}
            key="step5"
          >
            <CompleteStep onStartExploring={handleComplete} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      <Container size="md" padding fullHeight>
        <Stack direction="column" gap={6} fullHeight className={styles.content}>
          {/* Progress Bar - Hidden on welcome and complete steps */}
          {step > 1 && step < 5 && (
            <ProgressBar currentStep={step - 1} totalSteps={3} />
          )}

          {/* Step Content */}
          <div className={styles.stepContainer}>{renderStep()}</div>
        </Stack>
      </Container>
    </div>
  );
}

export default OnboardingPage;
